import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { MdContentCopy, MdRefresh } from 'react-icons/md';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import MipSyncModal from '~/components/Finance/MipSyncModal';
import {
    triggerMipSync,
    fetchSkyApiSettings,
    startSkyApiAuthorization,
    disconnectSkyApi,
    testSkyApiCustomFields,
    testSkyApiCategoryById,
    testSkyApiCategoryValues,
    testSkyApiFundCategories,
    testSkyApiFundCategoriesList,
    testSkyApiRecentFunds,
    testSkyApiFundById,
    testSkyApiCodeTables,
    testSkyApiCodeTableEntries,
} from '~/services/finance-services';
import type { SkyApiSettings } from '~/services/finance-services';

const FinancialSettings: React.FC = () => {
    // SKY API state
    const [skySettings, setSkySettings] = useState<SkyApiSettings | null>(null);
    const [skyLoading, setSkyLoading] = useState<boolean>(true);
    const [skyAuthorizing, setSkyAuthorizing] = useState<boolean>(false);
    const [skyDisconnecting, setSkyDisconnecting] = useState<boolean>(false);
    const [activeTestTab, setActiveTestTab] = useState<string>('custom-fields');
    const [useCache, setUseCache] = useState<boolean>(true);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number>(29);
    const [testingCustomFields, setTestingCustomFields] = useState<boolean>(false);
    
    // Category ID to name mapping
    const categoryIdToName: Record<number, string> = {
        29: 'Cat Type',
        26: 'Organ Tissue',
        27: 'Grant Goal',
        30: 'Region Code',
    };
    const [customFieldsResult, setCustomFieldsResult] = useState<{
        success: boolean;
        status_code: number | null;
        data: unknown;
        error: string | null;
        cached?: boolean;
        cached_at?: string;
    } | null>(null);
    const [recordTypeFilter, setRecordTypeFilter] = useState<string>('all');
    const [testingCatType, setTestingCatType] = useState<boolean>(false);
    const [catTypeResult, setCatTypeResult] = useState<{
        success: boolean;
        status_code: number | null;
        data: unknown;
        error: string | null;
        cached?: boolean;
        cached_at?: string;
    } | null>(null);
    const [testingCategoryValues, setTestingCategoryValues] = useState<boolean>(false);
    const [categoryValuesResult, setCategoryValuesResult] = useState<{
        success: boolean;
        status_code: number | null;
        data: unknown;
        error: string | null;
        cached?: boolean;
        cached_at?: string;
    } | null>(null);
    const [testingFundCategories, setTestingFundCategories] = useState<boolean>(false);
    const [fundCategoriesResult, setFundCategoriesResult] = useState<{
        success: boolean;
        status_code: number | null;
        data: unknown;
        error: string | null;
        cached?: boolean;
        cached_at?: string;
    } | null>(null);
    const [testingFundCategoriesList, setTestingFundCategoriesList] = useState<boolean>(false);
    const [fundCategoriesListResult, setFundCategoriesListResult] = useState<{
        success: boolean;
        status_code: number | null;
        data: unknown;
        error: string | null;
        cached?: boolean;
        cached_at?: string;
    } | null>(null);
    const [testingRecentFunds, setTestingRecentFunds] = useState<boolean>(false);
    const [recentFundsResult, setRecentFundsResult] = useState<{
        success: boolean;
        status_code: number | null;
        data: unknown;
        error: string | null;
        cached?: boolean;
        cached_at?: string;
        url?: string;
    } | null>(null);
    const [fundId, setFundId] = useState<string>('');
    const [fundStartDate, setFundStartDate] = useState<string>('');
    const [fundEndDate, setFundEndDate] = useState<string>('');
    const [fundLimit, setFundLimit] = useState<number>(3);
    const [testingCodeTables, setTestingCodeTables] = useState<boolean>(false);
    const [codeTablesResult, setCodeTablesResult] = useState<{
        success: boolean;
        status_code: number | null;
        data: unknown;
        error: string | null;
        cached?: boolean;
        cached_at?: string;
        url?: string;
    } | null>(null);
    const [codeTableName, setCodeTableName] = useState<string>('');
    const [codeTableIncludeHidden, setCodeTableIncludeHidden] = useState<boolean>(false);
    const [codeTableLimit, setCodeTableLimit] = useState<number>(10);
    const [codeTableOffset, setCodeTableOffset] = useState<number>(0);
    const [testingCodeTableEntries, setTestingCodeTableEntries] = useState<boolean>(false);
    const [codeTableEntriesResult, setCodeTableEntriesResult] = useState<{
        success: boolean;
        status_code: number | null;
        data: unknown;
        error: string | null;
        cached?: boolean;
        cached_at?: string;
        url?: string;
    } | null>(null);
    const [codeTableId, setCodeTableId] = useState<string>('');
    const [entryLongDescription, setEntryLongDescription] = useState<string>('');
    const [entryShortDescription, setEntryShortDescription] = useState<string>('');
    const [entryNumericValue, setEntryNumericValue] = useState<string>('');
    const [entryIncludeInactive, setEntryIncludeInactive] = useState<boolean>(false);
    const [entryLimit, setEntryLimit] = useState<number>(10);
    const [entryOffset, setEntryOffset] = useState<number>(0);

    // MIP Sync state
    const [mipSyncStartDate, setMipSyncStartDate] = useState('');
    const [mipSyncEndDate, setMipSyncEndDate] = useState('');
    const [mipSyncLoading, setMipSyncLoading] = useState(false);
    const [showMipSyncModal, setShowMipSyncModal] = useState(false);
    const [lastSyncResult, setLastSyncResult] = useState<{
        message: string;
        start_date: string;
        end_date: string;
        status: string;
        stats?: string;
        error?: string;
        success: boolean;
    } | null>(null);

    const loadSkySettings = useCallback(async () => {
        setSkyLoading(true);
        try {
            const settings = await fetchSkyApiSettings();
            setSkySettings(settings);
        } catch (error) {
            toast.error('Error loading SKY API settings');
            console.error('SKY API settings load error:', error);
        } finally {
            setSkyLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSkySettings();

        // Set default date range: yesterday to today for MIP sync
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const formatDateForInput = (date: Date) => date.toISOString().split('T')[0];

        setMipSyncStartDate(formatDateForInput(yesterday));
        setMipSyncEndDate(formatDateForInput(today));
    }, [loadSkySettings]);

    const handleStartSkyAuthorization = async () => {
        setSkyAuthorizing(true);
        try {
            const authorizationUrl = await startSkyApiAuthorization();
            window.open(authorizationUrl, '_blank', 'noopener,noreferrer');
            toast.success(
                'SKY API authorization opened in a new tab. Complete the login to finish connecting.'
            );
        } catch (error: unknown) {
            console.error('SKY API authorization error:', error);
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to start SKY API authorization';
            toast.error(message);
        } finally {
            setSkyAuthorizing(false);
        }
    };

    const handleDisconnectSky = async () => {
        if (!confirm('Disconnect SKY API and clear stored tokens?')) {
            return;
        }

        setSkyDisconnecting(true);
        try {
            const updated = await disconnectSkyApi();
            setSkySettings(updated);
            toast.success('SKY API disconnected');
        } catch (error) {
            console.error('SKY API disconnect error:', error);
            toast.error('Failed to disconnect SKY API');
        } finally {
            setSkyDisconnecting(false);
        }
    };

    const handleCopyCallback = async () => {
        const url = skySettings?.redirect_uri || skySettings?.suggested_redirect_uri;
        if (!url) {
            return;
        }

        try {
            await navigator.clipboard.writeText(url);
            toast.success('Callback URL copied to clipboard');
        } catch (error) {
            console.error('Clipboard copy failed:', error);
            toast.error('Unable to copy callback URL');
        }
    };

    const handleRefreshSkyStatus = () => {
        loadSkySettings();
    };

    const handleTestCustomFields = async () => {
        setTestingCustomFields(true);
        setCustomFieldsResult(null);
        setRecordTypeFilter('all'); // Reset filter when fetching new data
        console.log('=== Testing Custom Fields ===');
        console.log('Current sky settings:', skySettings);
        
        try {
            console.log('Calling testSkyApiCustomFields...');
            const result = await testSkyApiCustomFields(useCache);
            console.log('Response received:', result);
            console.log('Success:', result.success);
            console.log('Status code:', result.status_code);
            console.log('Error:', result.error);
            console.log('Data:', result.data);
            
            setCustomFieldsResult(result);
            
            if (result.success) {
                toast.success('Successfully fetched custom field categories');
                console.log('Refreshing settings after successful test...');
                // Refresh settings to show updated token expiration
                loadSkySettings();
            } else {
                const errorMsg = `Failed to fetch custom field categories: ${result.error || 'Unknown error'}`;
                console.error(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Custom fields test error (caught exception):', error);
            console.error('Error type:', typeof error);
            console.error('Error details:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error testing custom fields: ${errorMessage}`);
            setCustomFieldsResult({
                success: false,
                status_code: null,
                data: null,
                error: errorMessage,
            });
        } finally {
            setTestingCustomFields(false);
            console.log('=== Test Complete ===');
        }
    };

    const handleTestCatType = async () => {
        setTestingCatType(true);
        setCatTypeResult(null);
        console.log(`=== Testing Category by ID (ID ${selectedCategoryId}) ===`);
        console.log('Current sky settings:', skySettings);
        
        try {
            console.log(`Calling testSkyApiCategoryById with ID ${selectedCategoryId}...`);
            const result = await testSkyApiCategoryById(selectedCategoryId, useCache);
            console.log('Response received:', result);
            console.log('Success:', result.success);
            console.log('Status code:', result.status_code);
            console.log('Error:', result.error);
            console.log('Data:', result.data);
            
            setCatTypeResult(result);
            
            if (result.success) {
                toast.success(`Successfully fetched category ${selectedCategoryId}`);
                console.log('Refreshing settings after successful test...');
                loadSkySettings();
            } else {
                const errorMsg = `Failed to fetch category ${selectedCategoryId}: ${result.error || 'Unknown error'}`;
                console.error(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Category test error (caught exception):', error);
            console.error('Error type:', typeof error);
            console.error('Error details:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error testing category: ${errorMessage}`);
            setCatTypeResult({
                success: false,
                status_code: null,
                data: null,
                error: errorMessage,
            });
        } finally {
            setTestingCatType(false);
            console.log('=== Test Complete ===');
        }
    };

    const handleTestCategoryValues = async () => {
        const categoryName = categoryIdToName[selectedCategoryId];
        setTestingCategoryValues(true);
        setCategoryValuesResult(null);
        console.log(`=== Testing Category Values (${categoryName}) ===`);
        console.log('Current sky settings:', skySettings);
        
        try {
            console.log(`Calling testSkyApiCategoryValues with category_name="${categoryName}"...`);
            const result = await testSkyApiCategoryValues(categoryName, useCache);
            console.log('Response received:', result);
            console.log('Success:', result.success);
            console.log('Status code:', result.status_code);
            console.log('Error:', result.error);
            console.log('Data:', result.data);
            
            setCategoryValuesResult(result);
            
            if (result.success) {
                toast.success(`Successfully fetched ${categoryName} values`);
                console.log('Refreshing settings after successful test...');
                loadSkySettings();
            } else {
                const errorMsg = `Failed to fetch ${categoryName} values: ${result.error || 'Unknown error'}`;
                console.error(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Category values test error (caught exception):', error);
            console.error('Error type:', typeof error);
            console.error('Error details:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error testing category values: ${errorMessage}`);
            setCategoryValuesResult({
                success: false,
                status_code: null,
                data: null,
                error: errorMessage,
            });
        } finally {
            setTestingCategoryValues(false);
            console.log('=== Test Complete ===');
        }
    };

    const handleTestFundCategories = async () => {
        setTestingFundCategories(true);
        setFundCategoriesResult(null);
        console.log('=== Testing Fund Categories ===');
        console.log('Current sky settings:', skySettings);
        
        try {
            console.log('Calling testSkyApiFundCategories...');
            const result = await testSkyApiFundCategories(useCache);
            console.log('Response received:', result);
            console.log('Success:', result.success);
            console.log('Status code:', result.status_code);
            console.log('Error:', result.error);
            console.log('Data:', result.data);
            
            setFundCategoriesResult(result);
            
            if (result.success) {
                toast.success('Successfully fetched fund categories');
                console.log('Refreshing settings after successful test...');
                loadSkySettings();
            } else {
                const errorMsg = `Failed to fetch fund categories: ${result.error || 'Unknown error'}`;
                console.error(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Fund categories test error (caught exception):', error);
            console.error('Error type:', typeof error);
            console.error('Error details:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error testing fund categories: ${errorMessage}`);
            setFundCategoriesResult({
                success: false,
                status_code: null,
                data: null,
                error: errorMessage,
            });
        } finally {
            setTestingFundCategories(false);
            console.log('=== Test Complete ===');
        }
    };

    const handleTestFundCategoriesList = async () => {
        setTestingFundCategoriesList(true);
        setFundCategoriesListResult(null);
        console.log('=== Testing Fund Categories List ===');
        console.log('Current sky settings:', skySettings);
        
        try {
            console.log('Calling testSkyApiFundCategoriesList...');
            const result = await testSkyApiFundCategoriesList(useCache);
            console.log('Response received:', result);
            console.log('Success:', result.success);
            console.log('Status code:', result.status_code);
            console.log('Error:', result.error);
            console.log('Data:', result.data);
            
            setFundCategoriesListResult(result);
            
            if (result.success) {
                toast.success('Successfully fetched fund categories list');
                console.log('Refreshing settings after successful test...');
                loadSkySettings();
            } else {
                const errorMsg = `Failed to fetch fund categories list: ${result.error || 'Unknown error'}`;
                console.error(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Fund categories list test error (caught exception):', error);
            console.error('Error type:', typeof error);
            console.error('Error details:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error testing fund categories list: ${errorMessage}`);
            setFundCategoriesListResult({
                success: false,
                status_code: null,
                data: null,
                error: errorMessage,
            });
        } finally {
            setTestingFundCategoriesList(false);
            console.log('=== Test Complete ===');
        }
    };

    const handleTestRecentFunds = async () => {
        setTestingRecentFunds(true);
        setRecentFundsResult(null);
        
        // If fund ID is provided, use fund by ID endpoint
        if (fundId.trim()) {
            console.log('=== Testing Fund by ID ===');
            console.log('Fund ID:', fundId);
            console.log('Current sky settings:', skySettings);
            
            try {
                console.log('Calling testSkyApiFundById...');
                const result = await testSkyApiFundById(fundId.trim(), useCache);
                console.log('Response received:', result);
                console.log('Success:', result.success);
                console.log('Status code:', result.status_code);
                console.log('Error:', result.error);
                console.log('Data:', result.data);
                
                setRecentFundsResult(result);
                
                if (result.success) {
                    toast.success(`Successfully fetched fund ${fundId}`);
                    console.log('Refreshing settings after successful test...');
                    loadSkySettings();
                } else {
                    const errorMsg = `Failed to fetch fund ${fundId}: ${result.error || 'Unknown error'}`;
                    console.error(errorMsg);
                    toast.error(errorMsg);
                }
            } catch (error) {
                console.error('Fund by ID test error (caught exception):', error);
                console.error('Error type:', typeof error);
                console.error('Error details:', error);
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                toast.error(`Error testing fund by ID: ${errorMessage}`);
                setRecentFundsResult({
                    success: false,
                    status_code: null,
                    data: null,
                    error: errorMessage,
                });
            } finally {
                setTestingRecentFunds(false);
                console.log('=== Test Complete ===');
            }
            return;
        }
        
        // Otherwise, use date-based recent funds endpoint
        console.log('=== Testing Recent Funds ===');
        console.log('Current sky settings:', skySettings);
        
        // Calculate days from date range or use defaults
        let days = 365;
        if (fundStartDate && fundEndDate) {
            const start = new Date(fundStartDate);
            const end = new Date(fundEndDate);
            days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
            console.log('Using date range:', fundStartDate, 'to', fundEndDate, '=', days, 'days');
        }
        
        try {
            console.log('Calling testSkyApiRecentFunds...');
            const result = await testSkyApiRecentFunds(useCache, fundLimit, days);
            console.log('Response received:', result);
            console.log('Success:', result.success);
            console.log('Status code:', result.status_code);
            console.log('Error:', result.error);
            console.log('Data:', result.data);
            
            setRecentFundsResult(result);
            
            if (result.success) {
                toast.success('Successfully fetched recent funds');
                console.log('Refreshing settings after successful test...');
                loadSkySettings();
            } else {
                const errorMsg = `Failed to fetch recent funds: ${result.error || 'Unknown error'}`;
                console.error(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Recent funds test error (caught exception):', error);
            console.error('Error type:', typeof error);
            console.error('Error details:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error testing recent funds: ${errorMessage}`);
            setRecentFundsResult({
                success: false,
                status_code: null,
                data: null,
                error: errorMessage,
            });
        } finally {
            setTestingRecentFunds(false);
            console.log('=== Test Complete ===');
        }
    };

    const handleTestCodeTables = async () => {
        setTestingCodeTables(true);
        setCodeTablesResult(null);
        console.log('=== Testing Code Tables ===');
        console.log('Current sky settings:', skySettings);
        console.log('Name:', codeTableName || '(empty)');
        console.log('Include Hidden System:', codeTableIncludeHidden);
        console.log('Limit:', codeTableLimit);
        console.log('Offset:', codeTableOffset);
        
        try {
            console.log('Calling testSkyApiCodeTables...');
            const result = await testSkyApiCodeTables(
                codeTableName || undefined,
                codeTableIncludeHidden,
                codeTableLimit,
                codeTableOffset,
                useCache
            );
            console.log('Response received:', result);
            console.log('Success:', result.success);
            console.log('Status code:', result.status_code);
            console.log('Error:', result.error);
            console.log('Data:', result.data);
            
            setCodeTablesResult(result);
            
            if (result.success) {
                toast.success('Successfully fetched code tables');
                console.log('Refreshing settings after successful test...');
                loadSkySettings();
            } else {
                const errorMsg = `Failed to fetch code tables: ${result.error || 'Unknown error'}`;
                console.error(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Code tables test error (caught exception):', error);
            console.error('Error type:', typeof error);
            console.error('Error details:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error testing code tables: ${errorMessage}`);
            setCodeTablesResult({
                success: false,
                status_code: null,
                data: null,
                error: errorMessage,
            });
        } finally {
            setTestingCodeTables(false);
            console.log('=== Test Complete ===');
        }
    };

    const handleTestCodeTableEntries = async () => {
        if (!codeTableId.trim()) {
            toast.error('Code Table ID is required');
            return;
        }

        setTestingCodeTableEntries(true);
        setCodeTableEntriesResult(null);
        console.log('=== Testing Code Table Entries ===');
        console.log('Current sky settings:', skySettings);
        console.log('Code Table ID:', codeTableId);
        console.log('Long Description:', entryLongDescription || '(empty)');
        console.log('Short Description:', entryShortDescription || '(empty)');
        console.log('Numeric Value:', entryNumericValue || '(empty)');
        console.log('Include Inactive:', entryIncludeInactive);
        console.log('Limit:', entryLimit);
        console.log('Offset:', entryOffset);
        
        try {
            console.log('Calling testSkyApiCodeTableEntries...');
            const result = await testSkyApiCodeTableEntries(
                codeTableId.trim(),
                entryLongDescription || undefined,
                entryShortDescription || undefined,
                entryNumericValue ? Number(entryNumericValue) : undefined,
                entryIncludeInactive,
                entryLimit,
                entryOffset,
                useCache
            );
            console.log('Response received:', result);
            console.log('Success:', result.success);
            console.log('Status code:', result.status_code);
            console.log('Error:', result.error);
            console.log('Data:', result.data);
            
            setCodeTableEntriesResult(result);
            
            if (result.success) {
                toast.success(`Successfully fetched entries for code table ${codeTableId}`);
                console.log('Refreshing settings after successful test...');
                loadSkySettings();
            } else {
                const errorMsg = `Failed to fetch code table entries: ${result.error || 'Unknown error'}`;
                console.error(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Code table entries test error (caught exception):', error);
            console.error('Error type:', typeof error);
            console.error('Error details:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error testing code table entries: ${errorMessage}`);
            setCodeTableEntriesResult({
                success: false,
                status_code: null,
                data: null,
                error: errorMessage,
            });
        } finally {
            setTestingCodeTableEntries(false);
            console.log('=== Test Complete ===');
        }
    };

    // Helper to extract unique record types from custom fields data
    const getRecordTypes = (data: unknown): string[] => {
        if (!data || typeof data !== 'object') return [];
        
        // Assuming data is an array of custom field categories
        if (Array.isArray(data)) {
            const types = new Set<string>();
            data.forEach((item: Record<string, unknown>) => {
                if (item.record_type && typeof item.record_type === 'string') {
                    types.add(item.record_type);
                }
            });
            return Array.from(types).sort();
        }
        
        // If data has a 'value' array property (common in SKY API responses)
        if ('value' in data) {
            const dataWithValue = data as Record<string, unknown>;
            if (Array.isArray(dataWithValue.value)) {
                const types = new Set<string>();
                dataWithValue.value.forEach((item: Record<string, unknown>) => {
                    if (item.record_type && typeof item.record_type === 'string') {
                        types.add(item.record_type);
                    }
                });
                return Array.from(types).sort();
            }
        }
        
        return [];
    };

    // Helper to filter custom fields data by record type
    const filterByRecordType = (data: unknown, recordType: string): unknown => {
        if (!data || recordType === 'all') return data;
        if (typeof data !== 'object') return data;
        
        // If data is an array
        if (Array.isArray(data)) {
            return data.filter((item: Record<string, unknown>) => item.record_type === recordType);
        }
        
        // If data has a 'value' array property
        if ('value' in data) {
            const dataWithValue = data as Record<string, unknown>;
            if (Array.isArray(dataWithValue.value)) {
                return {
                    ...dataWithValue,
                    value: dataWithValue.value.filter((item: Record<string, unknown>) => item.record_type === recordType)
                };
            }
        }
        
        return data;
    };

    // Compute available record types from custom fields result
    const availableRecordTypes = useMemo(() => {
        if (!customFieldsResult?.success || !customFieldsResult.data) {
            return [];
        }
        return getRecordTypes(customFieldsResult.data);
    }, [customFieldsResult]);

    // Helper to format cache timestamp
    const formatCacheTime = (timestamp: string | undefined) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString();
        } catch {
            return '';
        }
    };

    // Helper to analyze fund response data
    const analyzeFundData = (data: unknown): { type: 'single' | 'list' | 'unknown'; count?: number; summary?: string } => {
        if (!data || typeof data !== 'object') {
            return { type: 'unknown' };
        }

        // Check if it's a single fund (has id, description, etc.)
        if ('id' in data && !Array.isArray(data)) {
            const fund = data as Record<string, unknown>;
            const parts = [];
            if (fund.id) parts.push(`ID: ${fund.id}`);
            if (fund.description) parts.push(`Description: ${fund.description}`);
            if (fund.fundraiser_status) parts.push(`Status: ${fund.fundraiser_status}`);
            return { 
                type: 'single',
                summary: parts.join(', ') || 'Single fund object'
            };
        }

        // Check if it's a list response (has 'value' array - SKY API format)
        if ('value' in data) {
            const listData = data as Record<string, unknown>;
            if (Array.isArray(listData.value)) {
                return { 
                    type: 'list',
                    count: listData.value.length,
                    summary: `${listData.value.length} fund${listData.value.length !== 1 ? 's' : ''} returned`
                };
            }
        }

        // Check if it's a direct array
        if (Array.isArray(data)) {
            return { 
                type: 'list',
                count: data.length,
                summary: `${data.length} fund${data.length !== 1 ? 's' : ''} returned`
            };
        }

        return { type: 'unknown' };
    };

    // Show MIP sync modal
    const handleShowMipSyncModal = () => {
        if (!mipSyncStartDate || !mipSyncEndDate) {
            toast.error('Both start date and end date are required');
            return;
        }

        const startDate = new Date(mipSyncStartDate + 'T00:00:00');
        const endDate = new Date(mipSyncEndDate + 'T00:00:00');

        if (startDate > endDate) {
            toast.error('Start date must be before end date');
            return;
        }

        setShowMipSyncModal(true);
    };

    // Handle MIP sync execution
    const handleMipSync = async () => {
        const formatDateForAPI = (dateString: string) => {
            const [year, month, day] = dateString.split('-').map(Number);
            const date = new Date(year, month - 1, day);

            const formattedMonth = (date.getMonth() + 1).toString().padStart(2, '0');
            const formattedDay = date.getDate().toString().padStart(2, '0');
            const formattedYear = date.getFullYear();
            return `${formattedMonth}/${formattedDay}/${formattedYear}`;
        };

        const formattedStartDate = formatDateForAPI(mipSyncStartDate);
        const formattedEndDate = formatDateForAPI(mipSyncEndDate);

        setMipSyncLoading(true);
        try {
            const result = await triggerMipSync(formattedStartDate, formattedEndDate);
            setLastSyncResult(result);

            // The sync runs in the background on the server; this only
            // confirms it started.
            toast.success(result.message);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Error running MIP sync');
            console.error('MIP sync error:', error);
        } finally {
            setMipSyncLoading(false);
        }
    };

    return (
        <>
            <Breadcrumb pageName="Financial Settings" />

            {/* Manual Disbursement Report Sync Section */}
            <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark mb-6">
                <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
                    <h3 className="font-medium text-black dark:text-white">
                        Manual Disbursement Report Sync
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Manually run the sync anytime a check is voided
                    </p>
                </div>

                <div className="p-7">
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                    Start Date *
                                </label>
                                <input
                                    type="date"
                                    value={mipSyncStartDate}
                                    onChange={(e) => setMipSyncStartDate(e.target.value)}
                                    className="w-full rounded border border-stroke bg-gray px-4 py-3 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                                    required
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                    End Date *
                                </label>
                                <input
                                    type="date"
                                    value={mipSyncEndDate}
                                    onChange={(e) => setMipSyncEndDate(e.target.value)}
                                    className="w-full rounded border border-stroke bg-gray px-4 py-3 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                                    required
                                />
                            </div>
                            <div>
                                <button
                                    onClick={handleShowMipSyncModal}
                                    disabled={mipSyncLoading || !mipSyncStartDate || !mipSyncEndDate}
                                    className="w-full rounded bg-primary px-4 py-3 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {mipSyncLoading ? 'Processing...' : 'RUN SYNC'}
                                </button>
                            </div>
                        </div>

                        {lastSyncResult && (
                            <div className="rounded border border-stroke bg-gray-2 p-4 text-sm dark:border-strokedark dark:bg-meta-4">
                                <h4 className="font-medium text-black dark:text-white mb-2">Last Sync Result</h4>
                                <div className={`space-y-1 ${
                                    lastSyncResult.success
                                        ? 'text-green-700 dark:text-green-300'
                                        : 'text-red-700 dark:text-red-300'
                                }`}>
                                    <p><strong>Message:</strong> {lastSyncResult.message}</p>
                                    <p><strong>Date Range:</strong> {lastSyncResult.start_date} to {lastSyncResult.end_date}</p>
                                    <p><strong>Status:</strong> {lastSyncResult.status}</p>
                                    {lastSyncResult.stats && (
                                        <p><strong>Details:</strong> {lastSyncResult.stats}</p>
                                    )}
                                    {lastSyncResult.error && (
                                        <p><strong>Error:</strong> {lastSyncResult.error}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                Instructions
                            </h4>
                            <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                                <li>Use this sync when a check has been voided and you need to update the disbursement data</li>
                                <li>Select the date range that covers the voided transactions</li>
                                <li>The sync runs sequentially to avoid overwhelming MIP connection limits</li>
                                <li>Processing time depends on the date range - larger ranges will take longer</li>
                                <li>The UI will show a loading indicator and display results when complete</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* SKY API Testing Section with Tabs */}
            <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark mb-6">
                <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
                    <h3 className="font-medium text-black dark:text-white">
                        SKY API Testing
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Test various SKY API endpoints (automatically refreshes access token if expired)
                    </p>
                </div>

                {/* Tabs */}
                <div className="border-b border-stroke dark:border-strokedark">
                    <div className="flex gap-2 px-7">
                        <button
                            onClick={() => setActiveTestTab('custom-fields')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTestTab === 'custom-fields'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            Custom Field Categories
                        </button>
                        <button
                            onClick={() => setActiveTestTab('cat-type')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTestTab === 'cat-type'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            Category by ID
                        </button>
                        <button
                            onClick={() => setActiveTestTab('cat-type-values')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTestTab === 'cat-type-values'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            Category Values
                        </button>
                        <button
                            onClick={() => setActiveTestTab('fund-categories')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTestTab === 'fund-categories'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            CustomField Categories
                        </button>
                        <button
                            onClick={() => setActiveTestTab('fund-categories-list')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTestTab === 'fund-categories-list'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            Fund Categories
                        </button>
                        <button
                            onClick={() => setActiveTestTab('recent-funds')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTestTab === 'recent-funds'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            Funds Search
                        </button>
                        <button
                            onClick={() => setActiveTestTab('code-tables')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTestTab === 'code-tables'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            Code Tables
                        </button>
                        <button
                            onClick={() => setActiveTestTab('code-table-entries')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTestTab === 'code-table-entries'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            Code Table Entries
                        </button>
                    </div>
                </div>

                {/* Cache Control */}
                <div className="px-7 pt-4 pb-2 bg-gray-50 dark:bg-gray-800/50 border-b border-stroke dark:border-strokedark">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                            type="checkbox"
                            checked={!useCache}
                            onChange={(e) => setUseCache(!e.target.checked)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-gray-700 dark:text-gray-300">
                            Skip cache (fetch fresh data)
                        </span>
                    </label>
                </div>

                {/* Tab Content */}
                <div className="p-7">
                    {/* Custom Field Categories Tab */}
                    {activeTestTab === 'custom-fields' && (
                        <div className="flex flex-col gap-4">
                            <div className="flex gap-3">
                                <button
                                    onClick={handleTestCustomFields}
                                    disabled={testingCustomFields || !skySettings?.has_refresh_token}
                                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {testingCustomFields ? 'Testing...' : 'Test Custom Field Categories'}
                                </button>
                                {!skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Connect SKY API first (see section below)
                                    </p>
                                )}
                                {skySettings?.has_refresh_token && !skySettings?.is_connected && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 self-center">
                                        Access token expired - will be refreshed automatically
                                    </p>
                                )}
                            </div>

                            {/* Record Type Filter */}
                            {availableRecordTypes.length > 0 && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Filter by Record Type:
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => setRecordTypeFilter('all')}
                                            className={`px-3 py-1 text-sm rounded transition-colors ${
                                                recordTypeFilter === 'all'
                                                    ? 'bg-primary text-white'
                                                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            All
                                        </button>
                                        {availableRecordTypes.map((type) => (
                                            <button
                                                key={type}
                                                onClick={() => setRecordTypeFilter(type)}
                                                className={`px-3 py-1 text-sm rounded transition-colors ${
                                                    recordTypeFilter === type
                                                        ? 'bg-primary text-white'
                                                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                                }`}
                                            >
                                                {type}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {customFieldsResult && (
                                <div className={`rounded border p-4 text-sm ${
                                    customFieldsResult.success
                                        ? 'border-green-300 bg-green-50 dark:border-green-500/60 dark:bg-green-900/20'
                                        : 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-900/20'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        customFieldsResult.success
                                            ? 'text-green-800 dark:text-green-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        Test Result
                                    </h4>
                                    <div className={`space-y-2 ${
                                        customFieldsResult.success
                                            ? 'text-green-700 dark:text-green-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        <p><strong>Status:</strong> {customFieldsResult.success ? 'Success' : 'Failed'}</p>
                                        {customFieldsResult.status_code && (
                                            <p><strong>HTTP Status:</strong> {customFieldsResult.status_code}</p>
                                        )}
                                        {customFieldsResult.cached && (
                                            <p className="flex items-center gap-2">
                                                <strong>Cached:</strong> 
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs">
                                                    ✓ Cached {formatCacheTime(customFieldsResult.cached_at)}
                                                </span>
                                            </p>
                                        )}
                                        {!customFieldsResult.cached && customFieldsResult.cached_at && (
                                            <p><strong>Fetched at:</strong> {formatCacheTime(customFieldsResult.cached_at)}</p>
                                        )}
                                        {customFieldsResult.error && (
                                            <p><strong>Error:</strong> {customFieldsResult.error}</p>
                                        )}
                                        {customFieldsResult.data !== null && customFieldsResult.data !== undefined && (
                                            <div>
                                                <p className="font-medium mb-1">Response Data{recordTypeFilter !== 'all' && ` (filtered: ${recordTypeFilter})`}:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-3 rounded text-xs overflow-auto max-h-60 border border-gray-300 dark:border-gray-600">
                                                    {typeof customFieldsResult.data === 'string' 
                                                        ? customFieldsResult.data 
                                                        : JSON.stringify(filterByRecordType(customFieldsResult.data, recordTypeFilter), null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    About This Endpoint
                                </h4>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                                    <li>GET /nxt-data-integration/v1/re/customfieldcategories</li>
                                    <li>Returns all custom field categories in Raiser's Edge NXT</li>
                                    <li>Useful for understanding available custom field structures</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Category by ID Tab */}
                    {activeTestTab === 'cat-type' && (
                        <div className="flex flex-col gap-4">
                            <div className="flex items-end gap-3">
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                        Category ID
                                    </label>
                                    <select
                                        value={selectedCategoryId}
                                        onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                                        className="rounded border border-stroke bg-gray px-4 py-2 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                                    >
                                        <option value={29}>29 - Cat Type</option>
                                        <option value={26}>26 - Organ Tissue</option>
                                        <option value={27}>27 - Grant Goal</option>
                                        <option value={30}>30 - Region Code</option>
                                    </select>
                                </div>
                                <button
                                    onClick={handleTestCatType}
                                    disabled={testingCatType || !skySettings?.has_refresh_token}
                                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {testingCatType ? 'Testing...' : 'Test Category'}
                                </button>
                                {!skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Connect SKY API first (see section below)
                                    </p>
                                )}
                                {skySettings?.has_refresh_token && !skySettings?.is_connected && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 self-center">
                                        Access token expired - will be refreshed automatically
                                    </p>
                                )}
                            </div>

                            {catTypeResult && (
                                <div className={`rounded border p-4 text-sm ${
                                    catTypeResult.success
                                        ? 'border-green-300 bg-green-50 dark:border-green-500/60 dark:bg-green-900/20'
                                        : 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-900/20'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        catTypeResult.success
                                            ? 'text-green-800 dark:text-green-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        Test Result
                                    </h4>
                                    <div className={`space-y-2 ${
                                        catTypeResult.success
                                            ? 'text-green-700 dark:text-green-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        <p><strong>Status:</strong> {catTypeResult.success ? 'Success' : 'Failed'}</p>
                                        {catTypeResult.status_code && (
                                            <p><strong>HTTP Status:</strong> {catTypeResult.status_code}</p>
                                        )}
                                        {catTypeResult.cached && (
                                            <p className="flex items-center gap-2">
                                                <strong>Cached:</strong> 
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs">
                                                    ✓ Cached {formatCacheTime(catTypeResult.cached_at)}
                                                </span>
                                            </p>
                                        )}
                                        {!catTypeResult.cached && catTypeResult.cached_at && (
                                            <p><strong>Fetched at:</strong> {formatCacheTime(catTypeResult.cached_at)}</p>
                                        )}
                                        {catTypeResult.error && (
                                            <p><strong>Error:</strong> {catTypeResult.error}</p>
                                        )}
                                        {catTypeResult.data !== null && catTypeResult.data !== undefined && (
                                            <div>
                                                <p className="font-medium mb-1">Response Data:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-3 rounded text-xs overflow-auto max-h-60 border border-gray-300 dark:border-gray-600">
                                                    {typeof catTypeResult.data === 'string' 
                                                        ? catTypeResult.data 
                                                        : JSON.stringify(catTypeResult.data, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    About This Endpoint
                                </h4>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                                    <li>GET /nxt-data-integration/v1/re/customfieldcategories/{'{'}category_id{'}'}</li>
                                    <li>Returns details for a specific custom field category by ID</li>
                                    <li>Select a category ID from the dropdown to test different categories</li>
                                    <li>Category 29 is "Cat Type" (Record Type: Fund, Data Type: Table)</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Category Values Tab */}
                    {activeTestTab === 'cat-type-values' && (
                        <div className="flex flex-col gap-4">
                            <div className="flex items-end gap-3">
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                        Category
                                    </label>
                                    <select
                                        value={selectedCategoryId}
                                        onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                                        className="rounded border border-stroke bg-gray px-4 py-2 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                                    >
                                        <option value={29}>29 - Cat Type</option>
                                        <option value={26}>26 - Organ Tissue</option>
                                        <option value={27}>27 - Grant Goal</option>
                                        <option value={30}>30 - Region Code</option>
                                    </select>
                                </div>
                                <button
                                    onClick={handleTestCategoryValues}
                                    disabled={testingCategoryValues || !skySettings?.has_refresh_token}
                                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {testingCategoryValues ? 'Testing...' : 'Test Category Values'}
                                </button>
                                {!skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Connect SKY API first (see section below)
                                    </p>
                                )}
                                {skySettings?.has_refresh_token && !skySettings?.is_connected && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 self-center">
                                        Access token expired - will be refreshed automatically
                                    </p>
                                )}
                            </div>

                            {categoryValuesResult && (
                                <div className={`rounded border p-4 text-sm ${
                                    categoryValuesResult.success
                                        ? 'border-green-300 bg-green-50 dark:border-green-500/60 dark:bg-green-900/20'
                                        : 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-900/20'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        categoryValuesResult.success
                                            ? 'text-green-800 dark:text-green-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        Test Result
                                    </h4>
                                    <div className={`space-y-2 ${
                                        categoryValuesResult.success
                                            ? 'text-green-700 dark:text-green-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        <p><strong>Status:</strong> {categoryValuesResult.success ? 'Success' : 'Failed'}</p>
                                        {categoryValuesResult.status_code && (
                                            <p><strong>HTTP Status:</strong> {categoryValuesResult.status_code}</p>
                                        )}
                                        {categoryValuesResult.cached && (
                                            <p className="flex items-center gap-2">
                                                <strong>Cached:</strong> 
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs">
                                                    ✓ Cached {formatCacheTime(categoryValuesResult.cached_at)}
                                                </span>
                                            </p>
                                        )}
                                        {!categoryValuesResult.cached && categoryValuesResult.cached_at && (
                                            <p><strong>Fetched at:</strong> {formatCacheTime(categoryValuesResult.cached_at)}</p>
                                        )}
                                        {categoryValuesResult.error && (
                                            <p><strong>Error:</strong> {categoryValuesResult.error}</p>
                                        )}
                                        {categoryValuesResult.data !== null && categoryValuesResult.data !== undefined && (
                                            <div>
                                                <p className="font-medium mb-1">Response Data:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-3 rounded text-xs overflow-auto max-h-60 border border-gray-300 dark:border-gray-600">
                                                    {typeof categoryValuesResult.data === 'string' 
                                                        ? categoryValuesResult.data 
                                                        : JSON.stringify(categoryValuesResult.data, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    About This Endpoint
                                </h4>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                                    <li>GET /fundraising/v1/funds/customfields/categories/values?category_name={'{'}category_name{'}'}</li>
                                    <li>Returns available values for the selected custom field category</li>
                                    <li>Select a category from the dropdown to test different category values</li>
                                    <li>Shows the actual selectable options for categorizing funds</li>
                                    <li>Used to understand valid values when creating or updating fund records</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Fund Categories Tab */}
                    {activeTestTab === 'fund-categories' && (
                        <div className="flex flex-col gap-4">
                            <div className="flex gap-3">
                                <button
                                    onClick={handleTestFundCategories}
                                    disabled={testingFundCategories || !skySettings?.has_refresh_token}
                                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {testingFundCategories ? 'Testing...' : 'Test Fund Categories'}
                                </button>
                                {!skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Connect SKY API first (see section below)
                                    </p>
                                )}
                                {skySettings?.has_refresh_token && !skySettings?.is_connected && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 self-center">
                                        Access token expired - will be refreshed automatically
                                    </p>
                                )}
                            </div>

                            {fundCategoriesResult && (
                                <div className={`rounded border p-4 text-sm ${
                                    fundCategoriesResult.success
                                        ? 'border-green-300 bg-green-50 dark:border-green-500/60 dark:bg-green-900/20'
                                        : 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-900/20'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        fundCategoriesResult.success
                                            ? 'text-green-800 dark:text-green-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        Test Result
                                    </h4>
                                    <div className={`space-y-2 ${
                                        fundCategoriesResult.success
                                            ? 'text-green-700 dark:text-green-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        <p><strong>Status:</strong> {fundCategoriesResult.success ? 'Success' : 'Failed'}</p>
                                        {fundCategoriesResult.status_code && (
                                            <p><strong>HTTP Status:</strong> {fundCategoriesResult.status_code}</p>
                                        )}
                                        {fundCategoriesResult.cached && (
                                            <p className="flex items-center gap-2">
                                                <strong>Cached:</strong> 
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs">
                                                    ✓ Cached {formatCacheTime(fundCategoriesResult.cached_at)}
                                                </span>
                                            </p>
                                        )}
                                        {!fundCategoriesResult.cached && fundCategoriesResult.cached_at && (
                                            <p><strong>Fetched at:</strong> {formatCacheTime(fundCategoriesResult.cached_at)}</p>
                                        )}
                                        {fundCategoriesResult.error && (
                                            <p><strong>Error:</strong> {fundCategoriesResult.error}</p>
                                        )}
                                        {fundCategoriesResult.data !== null && fundCategoriesResult.data !== undefined && (
                                            <div>
                                                <p className="font-medium mb-1">Response Data:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-3 rounded text-xs overflow-auto max-h-60 border border-gray-300 dark:border-gray-600">
                                                    {typeof fundCategoriesResult.data === 'string' 
                                                        ? fundCategoriesResult.data 
                                                        : JSON.stringify(fundCategoriesResult.data, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    About This Endpoint
                                </h4>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                                    <li>GET /fundraising/v1/funds/customfields/categories</li>
                                    <li>Returns custom field categories specifically for fund objects from the Fundraising API</li>
                                    <li>Shows available custom fields that can be used with funds</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Fund Categories List Tab */}
                    {activeTestTab === 'fund-categories-list' && (
                        <div className="flex flex-col gap-4">
                            <div className="flex gap-3">
                                <button
                                    onClick={handleTestFundCategoriesList}
                                    disabled={testingFundCategoriesList || !skySettings?.has_refresh_token}
                                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {testingFundCategoriesList ? 'Testing...' : 'Test Fund Categories'}
                                </button>
                                {!skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Connect SKY API first (see section below)
                                    </p>
                                )}
                                {skySettings?.has_refresh_token && !skySettings?.is_connected && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 self-center">
                                        Access token expired - will be refreshed automatically
                                    </p>
                                )}
                            </div>

                            {fundCategoriesListResult && (
                                <div className={`rounded border p-4 text-sm ${
                                    fundCategoriesListResult.success
                                        ? 'border-green-300 bg-green-50 dark:border-green-500/60 dark:bg-green-900/20'
                                        : 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-900/20'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        fundCategoriesListResult.success
                                            ? 'text-green-800 dark:text-green-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        Test Result
                                    </h4>
                                    <div className={`space-y-2 ${
                                        fundCategoriesListResult.success
                                            ? 'text-green-700 dark:text-green-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        <p><strong>Status:</strong> {fundCategoriesListResult.success ? 'Success' : 'Failed'}</p>
                                        {fundCategoriesListResult.status_code && (
                                            <p><strong>HTTP Status:</strong> {fundCategoriesListResult.status_code}</p>
                                        )}
                                        {fundCategoriesListResult.cached && (
                                            <p className="flex items-center gap-2">
                                                <strong>Cached:</strong> 
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs">
                                                    ✓ Cached {formatCacheTime(fundCategoriesListResult.cached_at)}
                                                </span>
                                            </p>
                                        )}
                                        {!fundCategoriesListResult.cached && fundCategoriesListResult.cached_at && (
                                            <p><strong>Fetched at:</strong> {formatCacheTime(fundCategoriesListResult.cached_at)}</p>
                                        )}
                                        {fundCategoriesListResult.error && (
                                            <p><strong>Error:</strong> {fundCategoriesListResult.error}</p>
                                        )}
                                        {fundCategoriesListResult.data !== null && fundCategoriesListResult.data !== undefined && (
                                            <div>
                                                <p className="font-medium mb-1">Response Data:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-3 rounded text-xs overflow-auto max-h-60 border border-gray-300 dark:border-gray-600">
                                                    {typeof fundCategoriesListResult.data === 'string' 
                                                        ? fundCategoriesListResult.data 
                                                        : JSON.stringify(fundCategoriesListResult.data, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    About This Endpoint
                                </h4>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                                    <li>GET /fundraising/v1/funds/categories</li>
                                    <li>Returns all fund categories available in the system</li>
                                    <li>Used to categorize and organize funds in Raiser's Edge NXT</li>
                                    <li>Different from custom field categories - this is for standard fund classification</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Recent Funds Tab */}
                    {activeTestTab === 'recent-funds' && (
                        <div className="flex flex-col gap-4">
                            {/* Fund ID Filter */}
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
                                <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
                                    Search by Fund ID
                                </h4>
                                <div className="flex items-end gap-3">
                                    <div className="flex-1">
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Fund ID (overrides date filters)
                                        </label>
                                        <input
                                            type="text"
                                            value={fundId}
                                            onChange={(e) => setFundId(e.target.value)}
                                            placeholder="Enter fund ID (e.g., 12345)"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                    Uses endpoint: GET /fundraising/v1/funds/{'{'}fund_id{'}'}
                                </p>
                            </div>

                            {/* Date Range Filters */}
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
                                <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
                                    Search by Date Range
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            value={fundStartDate}
                                            onChange={(e) => setFundStartDate(e.target.value)}
                                            disabled={!!fundId.trim()}
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary disabled:opacity-50"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            value={fundEndDate}
                                            onChange={(e) => setFundEndDate(e.target.value)}
                                            disabled={!!fundId.trim()}
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary disabled:opacity-50"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Result Limit
                                        </label>
                                        <input
                                            type="number"
                                            value={fundLimit}
                                            onChange={(e) => setFundLimit(Number(e.target.value))}
                                            min="1"
                                            max="100"
                                            disabled={!!fundId.trim()}
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary disabled:opacity-50"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                    {fundStartDate && fundEndDate 
                                        ? `Uses endpoint: GET /fundraising/v1/funds?limit=${fundLimit}&date_added={date_added}`
                                        : 'Default: Last 365 days. Uses endpoint: GET /fundraising/v1/funds?limit=3&date_added={date_added}'}
                                </p>
                            </div>

                            {/* Test Button */}
                            <div className="flex gap-3">
                                <button
                                    onClick={handleTestRecentFunds}
                                    disabled={testingRecentFunds || !skySettings?.has_refresh_token}
                                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {testingRecentFunds ? 'Testing...' : (fundId.trim() ? 'Fetch Fund by ID' : 'Fetch Recent Funds')}
                                </button>
                                {!skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Connect SKY API first (see section below)
                                    </p>
                                )}
                                {skySettings?.has_refresh_token && !skySettings?.is_connected && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 self-center">
                                        Access token expired - will be refreshed automatically
                                    </p>
                                )}
                            </div>

                            {recentFundsResult && (
                                <div className={`rounded border p-4 text-sm ${
                                    recentFundsResult.success
                                        ? 'border-green-300 bg-green-50 dark:border-green-500/60 dark:bg-green-900/20'
                                        : 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-900/20'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        recentFundsResult.success
                                            ? 'text-green-800 dark:text-green-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        Test Result
                                    </h4>
                                    <div className={`space-y-2 ${
                                        recentFundsResult.success
                                            ? 'text-green-700 dark:text-green-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        <p><strong>Status:</strong> {recentFundsResult.success ? 'Success' : 'Failed'}</p>
                                        {recentFundsResult.status_code && (
                                            <p><strong>HTTP Status:</strong> {recentFundsResult.status_code}</p>
                                        )}
                                        {recentFundsResult.url && (
                                            <div className="mb-2">
                                                <p className="font-medium mb-1">Request URL:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-2 rounded text-xs overflow-auto border border-gray-300 dark:border-gray-600">
                                                    {recentFundsResult.url}
                                                </pre>
                                            </div>
                                        )}
                                        {recentFundsResult.cached && (
                                            <p className="flex items-center gap-2">
                                                <strong>Cached:</strong> 
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs">
                                                    ✓ Cached {formatCacheTime(recentFundsResult.cached_at)}
                                                </span>
                                            </p>
                                        )}
                                        {!recentFundsResult.cached && recentFundsResult.cached_at && (
                                            <p><strong>Fetched at:</strong> {formatCacheTime(recentFundsResult.cached_at)}</p>
                                        )}
                                        {recentFundsResult.error && (
                                            <p><strong>Error:</strong> {recentFundsResult.error}</p>
                                        )}
                                        {recentFundsResult.data !== null && recentFundsResult.data !== undefined && (() => {
                                            const analysis = analyzeFundData(recentFundsResult.data);
                                            return (
                                                <div>
                                                    {analysis.summary && (
                                                        <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-700">
                                                            <p className="text-xs">
                                                                <strong>Response Type:</strong> {analysis.type === 'single' ? 'Single Fund' : analysis.type === 'list' ? 'Fund List' : 'Unknown'}
                                                                {analysis.count !== undefined && ` (${analysis.count} item${analysis.count !== 1 ? 's' : ''})`}
                                                            </p>
                                                            {analysis.type === 'single' && (
                                                                <p className="text-xs mt-1">{analysis.summary}</p>
                                                            )}
                                                        </div>
                                                    )}
                                                    <p className="font-medium mb-1">Full Response Data:</p>
                                                    <pre className="bg-white dark:bg-gray-800 p-3 rounded text-xs overflow-auto max-h-60 border border-gray-300 dark:border-gray-600">
                                                        {typeof recentFundsResult.data === 'string' 
                                                            ? recentFundsResult.data 
                                                            : JSON.stringify(recentFundsResult.data, null, 2)}
                                                    </pre>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    About These Endpoints
                                </h4>
                                <div className="space-y-3 text-sm text-blue-700 dark:text-blue-300">
                                    <div>
                                        <p className="font-medium mb-1">Fund by ID (when Fund ID is provided):</p>
                                        <ul className="list-disc list-inside space-y-1 ml-4">
                                            <li>GET /fundraising/v1/funds/{'{'}fund_id{'}'}</li>
                                            <li>Returns details for a specific fund by its ID</li>
                                            <li>Useful for inspecting individual fund records</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1">Recent Funds by Date (when no Fund ID):</p>
                                        <ul className="list-disc list-inside space-y-1 ml-4">
                                            <li>GET /fundraising/v1/funds?limit={'{'}limit{'}'}&date_added={'{'}date_added{'}'}</li>
                                            <li>Returns funds added within the specified date range</li>
                                            <li>Default: Last 365 days, limited to 3 results</li>
                                            <li>Use date filters to customize the range and limit</li>
                                            <li>Useful for exploring fund data structure and mapping fund_id to ntaf_id</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Code Tables Tab */}
                    {activeTestTab === 'code-tables' && (
                        <div className="flex flex-col gap-4">
                            {/* Parameter Inputs */}
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
                                <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
                                    Query Parameters
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Name (optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={codeTableName}
                                            onChange={(e) => setCodeTableName(e.target.value)}
                                            placeholder="Filter by table name"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            Filter to a specific code table by name
                                        </p>
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Limit
                                        </label>
                                        <input
                                            type="number"
                                            value={codeTableLimit}
                                            onChange={(e) => setCodeTableLimit(Number(e.target.value))}
                                            min="1"
                                            max="500"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            Max number of results to return (1-500)
                                        </p>
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Offset
                                        </label>
                                        <input
                                            type="number"
                                            value={codeTableOffset}
                                            onChange={(e) => setCodeTableOffset(Number(e.target.value))}
                                            min="0"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            Number of results to skip for pagination
                                        </p>
                                    </div>
                                    <div className="flex items-center">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={codeTableIncludeHidden}
                                                onChange={(e) => setCodeTableIncludeHidden(e.target.checked)}
                                                className="rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                Include hidden system tables
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Test Button */}
                            <div className="flex gap-3">
                                <button
                                    onClick={handleTestCodeTables}
                                    disabled={testingCodeTables || !skySettings?.has_refresh_token}
                                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {testingCodeTables ? 'Testing...' : 'Test Code Tables'}
                                </button>
                                {!skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Connect SKY API first (see section below)
                                    </p>
                                )}
                                {skySettings?.has_refresh_token && !skySettings?.is_connected && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 self-center">
                                        Access token expired - will be refreshed automatically
                                    </p>
                                )}
                            </div>

                            {codeTablesResult && (
                                <div className={`rounded border p-4 text-sm ${
                                    codeTablesResult.success
                                        ? 'border-green-300 bg-green-50 dark:border-green-500/60 dark:bg-green-900/20'
                                        : 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-900/20'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        codeTablesResult.success
                                            ? 'text-green-800 dark:text-green-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        Test Result
                                    </h4>
                                    <div className={`space-y-2 ${
                                        codeTablesResult.success
                                            ? 'text-green-700 dark:text-green-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        <p><strong>Status:</strong> {codeTablesResult.success ? 'Success' : 'Failed'}</p>
                                        {codeTablesResult.status_code && (
                                            <p><strong>HTTP Status:</strong> {codeTablesResult.status_code}</p>
                                        )}
                                        {codeTablesResult.url && (
                                            <div className="mb-2">
                                                <p className="font-medium mb-1">Request URL:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-2 rounded text-xs overflow-auto border border-gray-300 dark:border-gray-600">
                                                    {codeTablesResult.url}
                                                </pre>
                                            </div>
                                        )}
                                        {codeTablesResult.cached && (
                                            <p className="flex items-center gap-2">
                                                <strong>Cached:</strong> 
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs">
                                                    ✓ Cached {formatCacheTime(codeTablesResult.cached_at)}
                                                </span>
                                            </p>
                                        )}
                                        {!codeTablesResult.cached && codeTablesResult.cached_at && (
                                            <p><strong>Fetched at:</strong> {formatCacheTime(codeTablesResult.cached_at)}</p>
                                        )}
                                        {codeTablesResult.error && (
                                            <p><strong>Error:</strong> {codeTablesResult.error}</p>
                                        )}
                                        {codeTablesResult.data !== null && codeTablesResult.data !== undefined && (
                                            <div>
                                                <p className="font-medium mb-1">Response Data:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-3 rounded text-xs overflow-auto max-h-60 border border-gray-300 dark:border-gray-600">
                                                    {typeof codeTablesResult.data === 'string' 
                                                        ? codeTablesResult.data 
                                                        : JSON.stringify(codeTablesResult.data, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    About This Endpoint
                                </h4>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                                    <li>GET /nxt-data-integration/v1/re/codetables</li>
                                    <li>Returns code tables available in Raiser's Edge NXT</li>
                                    <li>Use the <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">name</code> parameter to filter for a specific table</li>
                                    <li>Use <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">limit</code> and <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">offset</code> for pagination</li>
                                    <li>Set <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">include_hidden_system</code> to include system tables</li>
                                    <li>Code tables contain lookup values for various entity properties</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Code Table Entries Tab */}
                    {activeTestTab === 'code-table-entries' && (
                        <div className="flex flex-col gap-4">
                            {/* Parameter Inputs */}
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
                                <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
                                    Code Table ID (Required)
                                </h4>
                                <div className="mb-4">
                                    <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                        Code Table ID *
                                    </label>
                                    <input
                                        type="text"
                                        value={codeTableId}
                                        onChange={(e) => setCodeTableId(e.target.value)}
                                        placeholder="Enter code table ID"
                                        className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                    />
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        Required: The ID of the code table to fetch entries from
                                    </p>
                                </div>
                            </div>

                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
                                <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
                                    Filter Parameters (Optional)
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Long Description
                                        </label>
                                        <input
                                            type="text"
                                            value={entryLongDescription}
                                            onChange={(e) => setEntryLongDescription(e.target.value)}
                                            placeholder="Filter by long description"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            Filter entries by long description
                                        </p>
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Short Description
                                        </label>
                                        <input
                                            type="text"
                                            value={entryShortDescription}
                                            onChange={(e) => setEntryShortDescription(e.target.value)}
                                            placeholder="Filter by short description"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            Filter entries by short description
                                        </p>
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Numeric Value
                                        </label>
                                        <input
                                            type="number"
                                            value={entryNumericValue}
                                            onChange={(e) => setEntryNumericValue(e.target.value)}
                                            placeholder="Filter by numeric value"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            Filter entries by numeric value
                                        </p>
                                    </div>
                                    <div className="flex items-center">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={entryIncludeInactive}
                                                onChange={(e) => setEntryIncludeInactive(e.target.checked)}
                                                className="rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                Include inactive entries
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700">
                                <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
                                    Pagination
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Limit
                                        </label>
                                        <input
                                            type="number"
                                            value={entryLimit}
                                            onChange={(e) => setEntryLimit(Number(e.target.value))}
                                            min="1"
                                            max="500"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            Max number of entries to return (1-500)
                                        </p>
                                    </div>
                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                                            Offset
                                        </label>
                                        <input
                                            type="number"
                                            value={entryOffset}
                                            onChange={(e) => setEntryOffset(Number(e.target.value))}
                                            min="0"
                                            className="w-full rounded border border-stroke bg-white dark:bg-meta-4 px-4 py-2 text-black dark:text-white focus:border-primary focus-visible:outline-none dark:border-strokedark dark:focus:border-primary"
                                        />
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            Number of entries to skip for pagination
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Test Button */}
                            <div className="flex gap-3">
                                <button
                                    onClick={handleTestCodeTableEntries}
                                    disabled={testingCodeTableEntries || !skySettings?.has_refresh_token || !codeTableId.trim()}
                                    className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {testingCodeTableEntries ? 'Testing...' : 'Test Code Table Entries'}
                                </button>
                                {!skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Connect SKY API first (see section below)
                                    </p>
                                )}
                                {skySettings?.has_refresh_token && !skySettings?.is_connected && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 self-center">
                                        Access token expired - will be refreshed automatically
                                    </p>
                                )}
                                {!codeTableId.trim() && skySettings?.has_refresh_token && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 self-center">
                                        Enter a Code Table ID to test
                                    </p>
                                )}
                            </div>

                            {codeTableEntriesResult && (
                                <div className={`rounded border p-4 text-sm ${
                                    codeTableEntriesResult.success
                                        ? 'border-green-300 bg-green-50 dark:border-green-500/60 dark:bg-green-900/20'
                                        : 'border-red-300 bg-red-50 dark:border-red-500/60 dark:bg-red-900/20'
                                }`}>
                                    <h4 className={`font-medium mb-2 ${
                                        codeTableEntriesResult.success
                                            ? 'text-green-800 dark:text-green-200'
                                            : 'text-red-800 dark:text-red-200'
                                    }`}>
                                        Test Result
                                    </h4>
                                    <div className={`space-y-2 ${
                                        codeTableEntriesResult.success
                                            ? 'text-green-700 dark:text-green-300'
                                            : 'text-red-700 dark:text-red-300'
                                    }`}>
                                        <p><strong>Status:</strong> {codeTableEntriesResult.success ? 'Success' : 'Failed'}</p>
                                        {codeTableEntriesResult.status_code && (
                                            <p><strong>HTTP Status:</strong> {codeTableEntriesResult.status_code}</p>
                                        )}
                                        {codeTableEntriesResult.url && (
                                            <div className="mb-2">
                                                <p className="font-medium mb-1">Request URL:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-2 rounded text-xs overflow-auto border border-gray-300 dark:border-gray-600">
                                                    {codeTableEntriesResult.url}
                                                </pre>
                                            </div>
                                        )}
                                        {codeTableEntriesResult.cached && (
                                            <p className="flex items-center gap-2">
                                                <strong>Cached:</strong> 
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs">
                                                    ✓ Cached {formatCacheTime(codeTableEntriesResult.cached_at)}
                                                </span>
                                            </p>
                                        )}
                                        {!codeTableEntriesResult.cached && codeTableEntriesResult.cached_at && (
                                            <p><strong>Fetched at:</strong> {formatCacheTime(codeTableEntriesResult.cached_at)}</p>
                                        )}
                                        {codeTableEntriesResult.error && (
                                            <p><strong>Error:</strong> {codeTableEntriesResult.error}</p>
                                        )}
                                        {codeTableEntriesResult.data !== null && codeTableEntriesResult.data !== undefined && (
                                            <div>
                                                <p className="font-medium mb-1">Response Data:</p>
                                                <pre className="bg-white dark:bg-gray-800 p-3 rounded text-xs overflow-auto max-h-60 border border-gray-300 dark:border-gray-600">
                                                    {typeof codeTableEntriesResult.data === 'string' 
                                                        ? codeTableEntriesResult.data 
                                                        : JSON.stringify(codeTableEntriesResult.data, null, 2)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                    About This Endpoint
                                </h4>
                                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                                    <li>GET /nxt-data-integration/v1/re/codetables/{'{'}code_table_id{'}'}/tableentries</li>
                                    <li>Returns entries for a specific code table by ID</li>
                                    <li>Use filter parameters to narrow down results by description or numeric value</li>
                                    <li>Set <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">include_inactive</code> to include inactive entries</li>
                                    <li>Use <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">limit</code> and <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">offset</code> for pagination</li>
                                    <li>Code table entries are the actual lookup values within a code table</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* General Info Box (shown for all tabs) */}
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded">
                        <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
                            General Information
                        </h4>
                        <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc list-inside space-y-1">
                            <li>All endpoints automatically refresh the access token if expired</li>
                            <li>Requires <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">SKY_API_SUBSCRIPTION_KEY</code> environment variable</li>
                            <li>Uses proper authentication headers (Bearer token and subscription key)</li>
                            <li>Full API responses are displayed for debugging</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* SKY API Integration Section */}
            <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
                    <h3 className="font-medium text-black dark:text-white">
                        SKY API Integration
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Configure OAuth credentials for the Blackbaud SKY API and connect the integration.
                    </p>
                </div>

                <div className="p-7">
                    {skyLoading ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-300">
                            Loading SKY API settings...
                        </div>
                    ) : skySettings ? (
                        <div className="space-y-6">
                            {!skySettings.is_configured && (
                                <div className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/60 dark:bg-amber-900/40 dark:text-amber-200">
                                    <strong>Environment configuration required.</strong> Set the variables
                                    <code className="mx-1 rounded bg-white px-1 py-0.5 text-xs text-amber-800 dark:bg-amber-800 dark:text-amber-100">SKY_API_CLIENT_ID</code>,
                                    <code className="mx-1 rounded bg-white px-1 py-0.5 text-xs text-amber-800 dark:bg-amber-800 dark:text-amber-100">SKY_API_CLIENT_SECRET</code>,
                                    and
                                    <code className="ml-1 rounded bg-white px-1 py-0.5 text-xs text-amber-800 dark:bg-amber-800 dark:text-amber-100">SKY_API_REDIRECT_URI</code>
                                    before initiating the OAuth flow.
                                </div>
                            )}

                            <div className="rounded border border-stroke bg-gray-2 p-4 text-sm dark:border-strokedark dark:bg-meta-4">
                                <h4 className="font-medium text-black dark:text-white mb-2">
                                    OAuth Configuration
                                </h4>
                                <ul className="space-y-2 text-gray-700 dark:text-gray-200">
                                    <li>
                                        <strong>Client ID:</strong> {skySettings.has_client_id ? 'Configured via environment' : 'Missing'}
                                    </li>
                                    <li>
                                        <strong>Client Secret:</strong> {skySettings.has_client_secret ? 'Configured via environment' : 'Missing'}
                                    </li>
                                    <li>
                                        <strong>Scopes:</strong> {skySettings.scopes || '(none configured)'}
                                    </li>
                                    <li>
                                        <div className="flex flex-col gap-1">
                                            <strong>Redirect URI:</strong>
                                            <div className="flex items-center gap-2">
                                                <code className="break-all rounded bg-white px-2 py-1 text-xs text-gray-700 dark:bg-meta-2 dark:text-gray-200">
                                                    {skySettings.redirect_uri || skySettings.suggested_redirect_uri}
                                                </code>
                                                <button
                                                    type="button"
                                                    onClick={handleCopyCallback}
                                                    className="inline-flex items-center gap-1 rounded border border-stroke px-2 py-1 text-xs text-black hover:bg-gray-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
                                                >
                                                    <MdContentCopy size={14} />
                                                    Copy
                                                </button>
                                            </div>
                                            {!skySettings.redirect_uri && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    Add the value above to <code>SKY_API_REDIRECT_URI</code> so the integration uses this callback.
                                                </p>
                                            )}
                                        </div>
                                    </li>
                                </ul>
                            </div>

                            <div className="rounded border border-stroke bg-gray-2 p-4 text-sm dark:border-strokedark dark:bg-meta-4">
                                <h4 className="font-medium text-black dark:text-white mb-2">
                                    Integration Status
                                </h4>
                                <div className="space-y-2 text-gray-700 dark:text-gray-200">
                                    <p>
                                        <strong>Status:</strong>{' '}
                                        {skySettings.is_connected
                                            ? <span className="text-green-600 dark:text-green-300">Connected</span>
                                            : <span className="text-amber-600 dark:text-amber-300">Not Connected</span>}
                                    </p>
                                    <p>
                                        <strong>Configured:</strong>{' '}
                                        {skySettings.is_configured ? 'Yes' : 'No'}
                                    </p>
                                    <p>
                                        <strong>Effective Redirect URI:</strong>{' '}
                                        {skySettings.effective_redirect_uri}
                                    </p>
                                    {skySettings.token_expires_at && (
                                        <p>
                                            <strong>Access Token Expires:</strong>{' '}
                                            {new Date(skySettings.token_expires_at).toLocaleString()}
                                        </p>
                                    )}
                                </div>
                                {skySettings.last_error && (
                                    <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/60 dark:bg-red-950/40 dark:text-red-200">
                                        <strong>Last Error:</strong> {skySettings.last_error}
                                    </div>
                                )}
                                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                    After clicking "Connect SKY API" a new tab will open at Blackbaud. Complete the
                                    authorization there, then return to this page and use "Refresh Status" to confirm the connection.
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <button
                                    onClick={handleStartSkyAuthorization}
                                    disabled={skyAuthorizing || !skySettings.is_configured}
                                    className="rounded border border-primary px-4 py-2 text-primary hover:bg-primary hover:text-white disabled:opacity-50"
                                >
                                    {skyAuthorizing ? 'Preparing...' : 'Connect SKY API'}
                                </button>
                                <button
                                    onClick={handleRefreshSkyStatus}
                                    disabled={skyLoading}
                                    className="inline-flex items-center gap-2 rounded border border-stroke px-4 py-2 text-black hover:bg-gray-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4 disabled:opacity-50"
                                >
                                    <MdRefresh size={18} />
                                    Refresh Status
                                </button>
                                <button
                                    onClick={handleDisconnectSky}
                                    disabled={skyDisconnecting || !skySettings.is_connected}
                                    className="rounded border border-red-500 px-4 py-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                >
                                    {skyDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm text-red-600 dark:text-red-300">
                            Failed to load SKY API settings. Please refresh the page.
                        </div>
                    )}
                </div>
            </div>

            <MipSyncModal
                isOpen={showMipSyncModal}
                onClose={() => setShowMipSyncModal(false)}
                onConfirm={handleMipSync}
                startDate={mipSyncStartDate}
                endDate={mipSyncEndDate}
            />
        </>
    );
};

export default FinancialSettings;
